import { readFile, stat } from "node:fs/promises";
import { basename, extname, isAbsolute, join } from "node:path";
import { z } from "zod";
import { MAX_FILE_SIZE_BYTES } from "@project/shared";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import { textResult } from "./helpers.js";

const IMAGE_MIME_BY_EXT: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
};

export function buildUploadAttachmentTool(connection: AgentConnection, config: AgentRunnerConfig) {
  return defineTool(
    "upload_attachment",
    "Upload an image file (e.g. a Playwright screenshot) as a task attachment AND post it to the task chat in one step — no follow-up post_to_chat call needed. Supports png/jpg/gif/webp.",
    {
      path: z
        .string()
        .describe("Path to the image file — absolute, or relative to the workspace root"),
      title: z
        .string()
        .optional()
        .describe("Short caption posted with the image (defaults to the file name)"),
    },
    async ({ path, title }) => {
      try {
        const filePath = isAbsolute(path) ? path : join(config.workspaceDir, path);
        const mimeType = IMAGE_MIME_BY_EXT[extname(filePath).toLowerCase()];
        if (!mimeType) {
          return textResult(
            `Unsupported file type "${extname(filePath) || "(none)"}". Supported: ${Object.keys(IMAGE_MIME_BY_EXT).join(", ")}`,
          );
        }

        const info = await stat(filePath).catch(() => null);
        if (!info?.isFile()) {
          return textResult(`File not found: ${filePath}`);
        }
        if (info.size > MAX_FILE_SIZE_BYTES) {
          return textResult(
            `File is ${info.size} bytes — exceeds the ${MAX_FILE_SIZE_BYTES} byte upload limit. Resize or crop the image first.`,
          );
        }

        const fileName = basename(filePath);
        const { fileId, uploadUrl } = await connection.call("requestFileUpload", {
          sessionId: connection.sessionId,
          fileName,
          mimeType,
          fileSize: info.size,
        });

        const body = await readFile(filePath);
        const res = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": mimeType },
          body,
        });
        if (!res.ok) {
          return textResult(
            `Upload to storage failed: HTTP ${res.status} ${await res.text().catch(() => "")}`,
          );
        }

        const result = await connection.call("confirmFileUpload", {
          sessionId: connection.sessionId,
          fileId,
          title,
        });

        return textResult(
          `Uploaded ${fileName} (${info.size} bytes) and posted it to the task chat${title ? ` with caption "${title}"` : ""}. File ID: ${result.fileId}`,
        );
      } catch (error) {
        return textResult(
          `Failed to upload attachment: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}
